/**
 * Sticker placement vs allowedBeatIds.
 *
 * Rule: for options with `allowedBeatIds`, the half-open sticker interval
 * [startTick, endTick) must be entirely covered by the union of those beats'
 * half-open intervals [beat.startTick, beat.endTick).
 * Options without allowedBeatIds may use any interval within source bounds.
 */
export function stickerIntervalCoveredByAllowedBeats(
  startTick: number,
  endTick: number,
  allowedBeats: readonly { startTick: number; endTick: number }[],
): boolean {
  if (!(Number.isInteger(startTick) && Number.isInteger(endTick)) || endTick <= startTick) {
    return false;
  }
  if (allowedBeats.length === 0) return false;

  const sorted = [...allowedBeats]
    .map((b) => ({ start: b.startTick, end: b.endTick }))
    .filter((b) => b.end > b.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  if (sorted.length === 0) return false;

  // Merge overlapping/adjacent allowed intervals.
  const merged: { start: number; end: number }[] = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (!last || interval.start > last.end) {
      merged.push({ ...interval });
    } else if (interval.end > last.end) {
      last.end = interval.end;
    }
  }

  let coveredUntil = startTick;
  for (const interval of merged) {
    if (interval.end <= coveredUntil) continue;
    if (interval.start > coveredUntil) return false;
    coveredUntil = interval.end;
    if (coveredUntil >= endTick) return true;
  }
  return coveredUntil >= endTick;
}
